import AssetStaffSelector from "./AssetStaffSelector";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft, Printer, MoveRight, Wrench, AlertTriangle, Boxes, ShieldOff,
  CheckCircle2, History, ImagePlus, Star, Trash2, Camera as CameraIcon, Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import { AssetImageUploader, ImageChip } from "@/components/AssetImageUploader";

import { hasCapability } from "@/lib/roles";
import ImageLightbox from "@/components/ImageLightbox";

const NGN = (v) => "₦" + (Number(v) || 0).toLocaleString();
const fmt = (s) => s ? new Date(s).toLocaleString() : "—";

const STATUS_STYLE = {
  serviceable: "bg-emerald-50 text-emerald-700 border-emerald-200",
  under_maintenance: "bg-amber-50 text-amber-700 border-amber-200",
  unserviceable: "bg-red-50 text-red-700 border-red-200",
  disposed: "bg-slate-100 text-slate-600 border-slate-200",
  lost: "bg-red-100 text-red-800 border-red-300",
};
const SEVERITY_STYLE = {
  low:      "bg-slate-50 text-slate-700 border-slate-200",
  medium:   "bg-amber-50 text-amber-700 border-amber-200",
  high:     "bg-orange-50 text-orange-700 border-orange-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

// Inline SVG placeholder — generated, no network request needed
const PLACEHOLDER = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'>
     <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
       <stop offset='0' stop-color='#1B2C5A'/><stop offset='1' stop-color='#14224A'/>
     </linearGradient></defs>
     <rect width='400' height='300' fill='url(#g)'/>
     <g fill='none' stroke='#9DCD3F' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' transform='translate(160 105)'>
       <rect x='0' y='0' width='80' height='65' rx='6'/>
       <circle cx='25' cy='25' r='9'/>
       <path d='M5 55l20-22 18 16 12-8 20 22'/>
     </g>
     <text x='200' y='220' fill='#9DCD3F' font-family='ui-sans-serif,system-ui' font-size='14' text-anchor='middle' letter-spacing='2'>NO ASSET PHOTO YET</text>
   </svg>`
);

const AssetProfile = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canManage = ((hasCapability(user, "asset.edit") || hasCapability(user, "asset.manage")));


const canDispose = hasCapability(user, "asset.dispose");
const canDelete = hasCapability(user, "asset.delete");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editStaffName, setEditStaffName] = useState("");
  const [moveStaffName, setMoveStaffName] = useState("");
  const [assetHistory, setAssetHistory] = useState([]);
  const [historySkip, setHistorySkip] = useState(0);
const [a, setA] = useState(null);
  const [movements, setMovements] = useState([]);
  const [faults, setFaults] = useState([]);
  const [maintenance, setMaintenance] = useState([]);

  // Dialog state
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveForm, setMoveForm] = useState({ location: "", reason: "" });
  const [faultOpen, setFaultOpen] = useState(false);
  const [faultForm, setFaultForm] = useState({ description: "", severity: "medium", photos: [] });
  const [maintOpen, setMaintOpen] = useState(false);
  const [maintForm, setMaintForm] = useState({ type: "service", description: "", cost: "", technician: "", completed: false, next_due: "", photos: [] });
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [disposeForm, setDisposeForm] = useState({ reason: "" });
  const [imgOpen, setImgOpen] = useState(false);

  // Lightbox state
  const [lbImages, setLbImages] = useState([]);
  const [lbIndex, setLbIndex] = useState(0);
  const [lbOpen, setLbOpen] = useState(false);
  const openLightbox = (images, index = 0) => { setLbImages(images); setLbIndex(index); setLbOpen(true); };

  const loadAll = async () => {
    try {
      const [aR, mR, fR, mtR, hR] = await Promise.all([
        api.get(`/assets/${id}`),
        api.get(`/assets/${id}/movements`),
        api.get(`/assets/${id}/faults`),
        api.get(`/assets/${id}/maintenance`),
        api.get(`/assets/${id}/history`),
      ]);
      setA(aR.data);
      setMovements(mR.data);
      setFaults(fR.data); setMaintenance(mtR.data); setAssetHistory(hR.data); setHistorySkip(0);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  if (!a) return <div className="text-[#475569]">Loading asset...</div>;


const openEdit = async () => {
    setEditForm(Object.fromEntries(["name", "category", "location", "assigned_to_staff_id", "purchase_date", "purchase_cost", "supplier", "model_number", "serial_number", "warranty_until", "condition", "status", "notes"].map((key) => [key, a[key] ?? ""])));
    setEditStaffName(a.assigned_to_staff_name || "");
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...editForm, purchase_cost: editForm.purchase_cost === "" ? null : Number(editForm.purchase_cost) };
      if (payload.assigned_to_staff_id === (a.assigned_to_staff_id || "")) delete payload.assigned_to_staff_id;
      await api.patch(`/assets/${id}`, payload);
      setEditOpen(false); toast.success("Asset updated"); await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEdit(false); }
  };
  const archiveAsset = async () => {
    if (!window.confirm(`Archive ${a.name}? All lifecycle history and photos will be preserved.`)) return;
    try { await api.delete(`/assets/${id}`); toast.success("Asset archived"); await loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const historyPage = async (skip) => {
    try { const { data } = await api.get(`/assets/${id}/history`, { params: { skip } }); setAssetHistory(data); setHistorySkip(skip); }
    catch (e) { toast.error(formatApiError(e)); }
  };
const move = async () => {
    try {
      await api.post(`/assets/${id}/move`, moveForm);
      toast.success("Asset moved"); setMoveOpen(false); setMoveForm({ location: "", reason: "" }); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reportFault = async () => {
    try {
      await api.post(`/assets/${id}/fault`, faultForm);
      toast.success("Fault reported"); setFaultOpen(false); setFaultForm({ description: "", severity: "medium", photos: [] }); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveFault = async (fid) => {
    try { await api.patch(`/assets/${id}/faults/${fid}/resolve`); toast.success("Resolved"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const addMaint = async () => {
    try {
      const payload = { ...maintForm, cost: maintForm.cost ? Number(maintForm.cost) : undefined };
      await api.post(`/assets/${id}/maintenance`, payload);
      toast.success("Maintenance logged"); setMaintOpen(false); setMaintForm({ type: "service", description: "", cost: "", technician: "", completed: false, next_due: "", photos: [] }); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeMaint = async (mid) => {
    try { await api.patch(`/assets/${id}/maintenance/${mid}/complete`); toast.success("Completed"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const dispose = async () => {
    if (!disposeForm.reason) return toast.error("Please provide a disposal reason");
    try { await api.patch(`/assets/${id}/dispose`, disposeForm); toast.success("Asset disposed"); setDisposeOpen(false); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  // Image management
  const uploadImages = async (kind, files) => {
    try {
      for (const f of files) {
        await api.post(`/assets/${id}/images`, { kind, name: f.name, type: f.type, data: f.data });
      }
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteImage = async (imgId) => {
    try { await api.delete(`/assets/${id}/images/${imgId}`); toast.success("Image removed"); await loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setPrimaryImage = async (imgId) => {
    try { await api.patch(`/assets/${id}/images/${imgId}/primary`); toast.success("Primary image updated"); await loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const tagCode = `ASSET:${a.asset_tag}`;
  const openFaultsCount = faults.filter(f => !f.resolved).length;
  const openMaintCount = maintenance.filter(m => !m.completed).length;
  const gallery = a.images || [];
  const mainImage = a.photo || (gallery[0] && gallery[0].data) || PLACEHOLDER;
  const hasRealPhoto = Boolean(a.photo || gallery.length > 0);

  return (
    <div className="space-y-6" data-testid="asset-profile-page">

<Dialog open={editOpen} onOpenChange={setEditOpen}><DialogContent className="max-w-2xl max-h-[85vh] overflow-auto"><DialogHeader><DialogTitle>Edit Asset — {a.asset_tag}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {["name", "category", "location", "purchase_date", "purchase_cost", "supplier", "model_number", "serial_number", "warranty_until"].map((key) => <div key={key}><Label>{key.replaceAll("_", " ")}</Label><Input type={key === "purchase_cost" ? "number" : ["purchase_date", "warranty_until"].includes(key) ? "date" : "text"} min={0} value={editForm[key] ?? ""} onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })} /></div>)}
          <div><Label>Quantity (read-only)</Label><p data-testid="asset-edit-quantity">{a.quantity ?? 1}</p></div>
          <div><Label>Assigned Staff</Label><AssetStaffSelector value={editForm.assigned_to_staff_id} name={editStaffName} onChange={(id, name) => { setEditForm({ ...editForm, assigned_to_staff_id: id }); setEditStaffName(name); }} /></div>
          {["condition", "status"].map((key) => <div key={key}><Label>{key}</Label><Select value={editForm[key] || ""} onValueChange={(value) => setEditForm({ ...editForm, [key]: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{(key === "condition" ? ["new", "excellent", "good", "fair", "poor"] : ["serviceable", "under_maintenance", "unserviceable", "lost"]).map((value) => <SelectItem key={value} value={value}>{value.replaceAll("_", " ")}</SelectItem>)}</SelectContent></Select></div>)}
          <div className="col-span-2"><Label>Notes</Label><Textarea value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
        </div><DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={saveEdit} disabled={savingEdit}>Save Changes</Button></DialogFooter>
      </DialogContent></Dialog>
{/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" onClick={() => nav("/app/assets")} className="text-[#1B2C5A]" data-testid="back-to-assets">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> All Assets
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (!a.archived) && (
            <Button variant="outline" onClick={openEdit} data-testid="asset-edit-btn">Edit Asset</Button>
          )}
          {canDelete && !a.archived && <Button variant="outline" onClick={archiveAsset} data-testid="asset-archive-btn">Archive Asset</Button>}
          {a.archived && <span className="text-slate-500">Archived</span>}

          {canManage && !a.archived && a.status !== "disposed" && (
            <>
              <Button
                onClick={() => setImgOpen(true)}
                className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A] font-medium shadow-sm"
                data-testid="manage-images-btn"
              >
                <ImagePlus className="w-4 h-4 mr-1.5" />{" "}
                {hasRealPhoto ? "Manage Photos" : "Add Photos"}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setMoveForm({ location: a.location || "", reason: "" }); setMoveStaffName(a.assigned_to_staff_name || ""); setMoveOpen(true); }}
                data-testid="move-btn"
              >
                <MoveRight className="w-4 h-4 mr-1.5" /> Move / Assign
              </Button>
              <Button
                variant="outline"
                onClick={() => setMaintOpen(true)}
                data-testid="maint-btn"
              >
                <Wrench className="w-4 h-4 mr-1.5" /> Log Maintenance
              </Button>
            </>
          )}
          {canManage && !a.archived && <Button
            variant="outline"
            onClick={() => setFaultOpen(true)}
            className="border-amber-200 text-amber-800"
            data-testid="fault-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Report Fault
          </Button>}
          {(canDispose && !a.archived) && a.status !== "disposed" && (
            <Button variant="outline" onClick={() => setDisposeOpen(true)} className="border-red-200 text-red-700" data-testid="dispose-btn"><ShieldOff className="w-4 h-4 mr-1.5" /> Dispose</Button>
          )}
          <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Print Tag</Button>
        </div>
      </div>

      {/* Hero header — main photo + QR + meta */}
      <Card className="overflow-hidden border-[#E2E8F0]">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,0.9fr)]">
          {/* Main image */}
          <button
            type="button"
            onClick={() => {
              if (hasRealPhoto) {
                openLightbox(gallery.length ? gallery : [{ data: a.photo, name: a.name }], 0);
              } else if ((canManage && !a.archived) && a.status !== "disposed") {
                setImgOpen(true);
              }
            }}
            className="relative group bg-[#1B2C5A] aspect-[4/3] lg:aspect-auto lg:min-h-[280px] overflow-hidden cursor-pointer"
            data-testid="asset-main-image"
            aria-label={hasRealPhoto ? "View asset image" : "Add asset photo"}
          >
            <img
              src={mainImage}
              alt={a.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {(!hasRealPhoto && canManage) && (!a.archived) && (
              <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none">
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#9DCD3F] text-[#1B2C5A] text-sm font-medium shadow-lg group-hover:scale-105 transition-transform" data-testid="empty-state-upload">
                  <ImagePlus className="w-4 h-4" /> Click here to add a photo
                </span>
              </div>
            )}
            {hasRealPhoto && (
              <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs opacity-0 group-hover:opacity-100 transition pointer-events-none">
                Click to expand
              </div>
            )}
          </button>

          {/* Title block */}
          <div className="p-6 flex flex-col justify-center">
            <div className="overline text-[#9DCD3F]">{a.category}</div>
            <h1 className="font-display text-3xl text-[#1B2C5A] mt-1 leading-tight">{a.name}</h1>
            <p className="text-sm text-[#475569] mt-1">{a.location || "Unassigned location"} · Qty {a.quantity}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={"text-xs px-2.5 py-1 rounded-full border " + (STATUS_STYLE[a.status] || "")}>{(a.status || "").replace(/_/g, " ")}</span>
              <span className="text-xs px-2.5 py-1 rounded-full border bg-[#1B2C5A]/5 text-[#1B2C5A] border-[#1B2C5A]/15">{a.condition}</span>
              {a.assigned_to_staff_name && <span className="text-xs px-2.5 py-1 rounded-full border bg-[#9DCD3F]/15 text-[#1B2C5A] border-[#9DCD3F]/40">Assigned: {a.assigned_to_staff_name}</span>}
              {openFaultsCount > 0 && <span className="text-xs px-2.5 py-1 rounded-full border bg-red-50 text-red-700 border-red-200">{openFaultsCount} open fault{openFaultsCount > 1 ? "s" : ""}</span>}
              {openMaintCount > 0 && <span className="text-xs px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200">{openMaintCount} maintenance task{openMaintCount > 1 ? "s" : ""}</span>}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-5">
              <Stat label="Purchase Date" value={a.purchase_date || "—"} />
              <Stat label="Purchase Cost" value={a.purchase_cost ? NGN(a.purchase_cost) : "—"} />
              <Stat label="Total Value" value={NGN((a.purchase_cost || 0) * (a.quantity || 1))} />
              <Stat label="Warranty Until" value={a.warranty_until || "—"} />
            </div>
          </div>

          {/* QR */}
          <div className="p-6 border-t lg:border-t-0 lg:border-l border-[#E2E8F0] bg-[#FDFBF7] flex flex-col items-center justify-center">
            <div className="bg-white p-3 rounded-xl border-2 border-[#9DCD3F]/40">
              <QRCodeSVG value={tagCode} size={130} bgColor="#FFFFFF" fgColor="#1B2C5A" />
            </div>
            <div className="mt-2 font-mono text-xs text-[#1B2C5A]" data-testid="asset-tag">{a.asset_tag}</div>
            <div className="overline text-[#9DCD3F] mt-3 text-center">Scan to verify</div>
          </div>
        </div>

        {/* Gallery strip */}
        {gallery.length > 0 && (
          <div className="border-t border-[#E2E8F0] bg-[#FDFBF7] p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-[#1B2C5A] inline-flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#9DCD3F]" /> Photo Gallery ({gallery.length})</h4>
              {(canManage && !a.archived) && (
                <Button size="sm" variant="ghost" onClick={() => setImgOpen(true)} className="text-[#1B2C5A]"><ImagePlus className="w-3.5 h-3.5 mr-1.5" /> Add</Button>
              )}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" data-testid="asset-gallery">
              {gallery.map((img, i) => {
                const isPrimary = (a.photo_meta?.id) === img.id;
                return (
                  <div key={img.id} className="relative group">
                    <button
                      onClick={() => openLightbox(gallery, i)}
                      className="w-full aspect-square rounded-lg overflow-hidden border border-[#E2E8F0] bg-white hover:border-[#9DCD3F] transition"
                      data-testid={`gallery-thumb-${img.id}`}
                    >
                      <img src={img.data} alt={img.name || "asset photo"} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                    {isPrimary && (
                      <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#9DCD3F] text-[#1B2C5A] font-medium inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5" fill="currentColor" /> Main</span>
                    )}
                    {(canManage && !a.archived) && (
                      <div className="absolute inset-x-1 bottom-1 opacity-0 group-hover:opacity-100 transition flex gap-1 justify-center">
                        {!isPrimary && (
                          <button onClick={() => setPrimaryImage(img.id)} title="Set as main" className="px-1.5 py-1 rounded-md bg-[#1B2C5A] text-white text-[10px] hover:bg-[#14224A]" data-testid={`gallery-primary-${img.id}`}>
                            <Star className="w-3 h-3" />
                          </button>
                        )}
                        <button onClick={() => deleteImage(img.id)} title="Remove" className="px-1.5 py-1 rounded-md bg-red-600 text-white text-[10px] hover:bg-red-700" data-testid={`gallery-delete-${img.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white border border-[#E2E8F0] flex-wrap">

<TabsTrigger value="history" data-testid="tab-history">Asset History</TabsTrigger>
<TabsTrigger value="overview"><Boxes className="w-3.5 h-3.5 mr-1.5" /> Overview</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements"><History className="w-3.5 h-3.5 mr-1.5" /> Movements ({movements.length})</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench className="w-3.5 h-3.5 mr-1.5" /> Maintenance ({maintenance.length})</TabsTrigger>
          <TabsTrigger value="faults" data-testid="tab-faults"><AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Faults ({faults.length})</TabsTrigger>
        </TabsList>


<TabsContent value="history" className="mt-4"><Card className="p-4 space-y-3">
          {!assetHistory.length && <p>No audit events recorded. Existing moves, faults and maintenance remain in their tabs.</p>}
          {assetHistory.map((event) => <div key={event.id} className="border-b pb-3"><p className="font-medium">{event.action} · {fmt(event.at)}</p><p className="text-sm text-slate-500">{event.actor_name || event.actor_email || event.actor_id}</p>
            {event.meta?.before && Object.keys(event.meta.before).map((key) => <p key={key} className="text-sm">{key.replaceAll("_", " ")}: {key === "photo" ? "Photo changed" : `${event.meta.before[key] ?? "—"} → ${event.meta.after?.[key] ?? "—"}`}</p>)}
            {!event.meta?.before && <p className="text-sm break-all">{JSON.stringify(event.meta || {})}</p>}
          </div>)}
          <div className="flex gap-2"><Button variant="outline" disabled={!historySkip} onClick={() => historyPage(Math.max(0, historySkip - 100))}>Previous</Button><Button variant="outline" disabled={assetHistory.length < 100} onClick={() => historyPage(historySkip + 100)}>Next</Button></div>
        </Card></TabsContent>
<TabsContent value="overview" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <h3 className="font-display text-lg text-[#1B2C5A] flex items-center gap-2 before:content-[''] before:w-1 before:h-5 before:bg-[#9DCD3F] before:rounded-full">Asset Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <Detail label="Asset Tag" value={a.asset_tag} mono />
              <Detail label="Model Number" value={a.model_number} />
              <Detail label="Serial Number" value={a.serial_number} mono />
              <Detail label="Supplier" value={a.supplier} />
              <Detail label="Location" value={a.location} />
              <Detail label="Assigned Staff" value={a.assigned_to_staff_name} />
              <Detail label="Condition" value={a.condition} />
              <Detail label="Status" value={(a.status || "").replace(/_/g, " ")} />
              <Detail label="Created By" value={a.created_by} />
              {a.disposed_reason && <Detail label="Disposal Reason" value={a.disposed_reason} full />}
              {a.notes && <Detail label="Notes" value={a.notes} full />}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <h3 className="font-display text-lg text-[#1B2C5A]">Movement & Assignment History</h3>
            {movements.length === 0 ? (
              <p className="text-sm text-[#475569] mt-4">No movements recorded.</p>
            ) : (
              <ol className="mt-4 space-y-3 border-l-2 border-[#9DCD3F]/30 pl-4">
                {movements.map(m => (
                  <li key={m.id} className="relative" data-testid={`movement-${m.id}`}>
                    <span className="absolute -left-[1.4rem] top-1.5 w-3 h-3 rounded-full bg-[#9DCD3F] border-2 border-white" />
                    <div className="text-xs text-[#475569]">{fmt(m.at)} · by {m.by_name}</div>
                    <div className="text-sm text-[#1B2C5A] mt-0.5">
                      <strong>{m.from_location || "—"}</strong> <MoveRight className="inline w-3 h-3 text-[#9DCD3F]" /> <strong>{m.to_location || "—"}</strong>
                    </div>
                    {(m.from_assignee || m.to_assignee) && (
                      <div className="text-xs text-[#475569] mt-1">Assignee: {m.from_assignee || "—"} → {m.to_assignee || "—"}</div>
                    )}
                    {m.reason && <div className="text-xs text-[#475569] mt-1 italic">"{m.reason}"</div>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="maintenance" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <h3 className="font-display text-lg text-[#1B2C5A]">Maintenance History</h3>
            {maintenance.length === 0 ? (
              <p className="text-sm text-[#475569] mt-4">No maintenance records.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {maintenance.map(m => (
                  <div key={m.id} className="p-4 rounded-xl border border-[#E2E8F0] bg-white" data-testid={`maint-${m.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#1B2C5A]/5 text-[#1B2C5A] capitalize">{m.type}</span>
                          {m.completed
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Completed</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Open</span>}
                        </div>
                        <p className="text-sm text-[#1B2C5A] mt-2">{m.description}</p>
                        <div className="text-xs text-[#475569] mt-1.5">
                          {m.technician && <>Technician: <strong>{m.technician}</strong> · </>}
                          {m.cost && <>Cost: <strong>{NGN(m.cost)}</strong> · </>}
                          Logged: {fmt(m.created_at)} by {m.by_name}
                          {m.completed_at && <> · Completed: {fmt(m.completed_at)}</>}
                        </div>
                        {m.photos && m.photos.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap" data-testid={`maint-photos-${m.id}`}>
                            {m.photos.map((p, i) => (
                              <button key={p.id} onClick={() => openLightbox(m.photos, i)} className="rounded-md overflow-hidden border border-[#E2E8F0] hover:border-[#9DCD3F] transition">
                                <img src={p.data} alt={p.name} className="w-14 h-14 object-cover" loading="lazy" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {(!m.completed && canManage) && (!a.archived) && (
                        <Button size="sm" onClick={() => completeMaint(m.id)} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid={`maint-complete-${m.id}`}>Mark Complete</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="faults" className="mt-4">
          <Card className="p-6 border-[#E2E8F0]">
            <h3 className="font-display text-lg text-[#1B2C5A]">Fault Reports</h3>
            {faults.length === 0 ? (
              <p className="text-sm text-[#475569] mt-4">No faults reported. ✨</p>
            ) : (
              <div className="mt-4 space-y-3">
                {faults.map(f => (
                  <div key={f.id} className="p-4 rounded-xl border border-[#E2E8F0] bg-white" data-testid={`fault-${f.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={"text-xs px-2 py-0.5 rounded-full border capitalize " + (SEVERITY_STYLE[f.severity] || "")}>{f.severity}</span>
                          {f.resolved
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Resolved</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">Open</span>}
                        </div>
                        <p className="text-sm text-[#1B2C5A] mt-2">{f.description}</p>
                        <div className="text-xs text-[#475569] mt-1.5">
                          By {f.reported_by_name} · {fmt(f.reported_at)}
                          {f.resolved && <> · Resolved by {f.resolved_by_name} at {fmt(f.resolved_at)}</>}
                        </div>
                        {f.photos && f.photos.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap" data-testid={`fault-photos-${f.id}`}>
                            {f.photos.map((p, i) => (
                              <button key={p.id} onClick={() => openLightbox(f.photos, i)} className="rounded-md overflow-hidden border border-[#E2E8F0] hover:border-amber-400 transition">
                                <img src={p.data} alt={p.name} className="w-14 h-14 object-cover" loading="lazy" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {(!f.resolved && canManage) && (!a.archived) && (
                        <Button size="sm" onClick={() => resolveFault(f.id)} className="bg-[#1B2C5A] text-white" data-testid={`fault-resolve-${f.id}`}>Mark Resolved</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---------- Dialogs ---------- */}

      {/* Image Manager Dialog */}
      <Dialog open={imgOpen} onOpenChange={setImgOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Asset Photos</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <AssetImageUploader multiple onPick={(files) => uploadImages("gallery", files)} label="Upload asset photos" />
            <p className="text-xs text-[#475569]">Up to 12 photos per asset · auto-resized to 1280px for fast loading. The first image (or one you mark as Main) is shown on the profile header.</p>
            {gallery.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-72 overflow-y-auto pr-1">
                {gallery.map((img) => {
                  const isPrimary = (a.photo_meta?.id) === img.id;
                  return (
                    <div key={img.id} className="relative">
                      <img src={img.data} className="w-full aspect-square rounded-md object-cover border border-[#E2E8F0]" alt={img.name} loading="lazy" />
                      {isPrimary && <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#9DCD3F] text-[#1B2C5A] font-medium">Main</span>}
                      <div className="absolute inset-x-1 bottom-1 flex gap-1 justify-center">
                        {!isPrimary && <button onClick={() => setPrimaryImage(img.id)} className="px-1.5 py-1 rounded-md bg-[#1B2C5A] text-white text-[10px] hover:bg-[#14224A]"><Star className="w-3 h-3" /></button>}
                        <button onClick={() => deleteImage(img.id)} className="px-1.5 py-1 rounded-md bg-red-600 text-white text-[10px] hover:bg-red-700"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImgOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Move / Reassign Asset</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <Field label="New Location"><Input value={moveForm.location} onChange={(e) => setMoveForm({ ...moveForm, location: e.target.value })} placeholder={a.location || "e.g., JSS 2 Classroom B"} data-testid="move-location" /></Field>
            <Field label="Assigned Staff (Optional)"><AssetStaffSelector value={moveForm.assigned_to_staff_id === undefined ? a.assigned_to_staff_id : moveForm.assigned_to_staff_id} name={moveStaffName} onChange={(id, name) => { setMoveForm({ ...moveForm, assigned_to_staff_id: id }); setMoveStaffName(name); }} /></Field>
            <Field label="Reason"><Textarea rows={2} value={moveForm.reason} onChange={(e) => setMoveForm({ ...moveForm, reason: e.target.value })} placeholder="Why is this being moved?" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button onClick={move} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="move-submit">Confirm Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fault Dialog */}
      <Dialog open={faultOpen} onOpenChange={setFaultOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Report Fault / Damage</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <Field label="Severity">
              <Select value={faultForm.severity} onValueChange={(v) => setFaultForm({ ...faultForm, severity: v })}>
                <SelectTrigger data-testid="fault-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description"><Textarea rows={4} value={faultForm.description} onChange={(e) => setFaultForm({ ...faultForm, description: e.target.value })} placeholder="What's wrong with the asset?" data-testid="fault-description" /></Field>
            <Field label="Attach Photos (Optional)">
              <AssetImageUploader
                compact
                multiple
                label="Attach damage photos"
                onPick={(files) => setFaultForm((f) => ({ ...f, photos: [...(f.photos || []), ...files].slice(0, 6) }))}
              />
              {faultForm.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="fault-photos-preview">
                  {faultForm.photos.map((p, i) => (
                    <ImageChip key={i} src={p.data} name={p.name} onRemove={() => setFaultForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} />
                  ))}
                </div>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaultOpen(false)}>Cancel</Button>
            <Button onClick={reportFault} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="fault-submit">Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintOpen} onOpenChange={setMaintOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Log Maintenance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <Field label="Type">
              <Select value={maintForm.type} onValueChange={(v) => setMaintForm({ ...maintForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Technician"><Input value={maintForm.technician} onChange={(e) => setMaintForm({ ...maintForm, technician: e.target.value })} /></Field>
            <Field label="Cost (₦)"><Input type="number" value={maintForm.cost} onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })} /></Field>
            <Field label="Next Due"><Input type="date" value={maintForm.next_due} onChange={(e) => setMaintForm({ ...maintForm, next_due: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Description"><Textarea rows={3} value={maintForm.description} onChange={(e) => setMaintForm({ ...maintForm, description: e.target.value })} data-testid="maint-description" /></Field></div>
            <div className="col-span-2">
              <Field label="Attach Photos (Optional)">
                <AssetImageUploader
                  compact
                  multiple
                  label="Attach work photos"
                  onPick={(files) => setMaintForm((m) => ({ ...m, photos: [...(m.photos || []), ...files].slice(0, 6) }))}
                />
                {maintForm.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="maint-photos-preview">
                    {maintForm.photos.map((p, i) => (
                      <ImageChip key={i} src={p.data} name={p.name} onRemove={() => setMaintForm((m) => ({ ...m, photos: m.photos.filter((_, j) => j !== i) }))} />
                    ))}
                  </div>
                )}
              </Field>
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-[#1B2C5A]">
              <input type="checkbox" checked={maintForm.completed} onChange={(e) => setMaintForm({ ...maintForm, completed: e.target.checked })} data-testid="maint-completed" />
              Mark as already completed
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintOpen(false)}>Cancel</Button>
            <Button onClick={addMaint} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="maint-submit">Log Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispose Dialog */}
      <Dialog open={disposeOpen} onOpenChange={setDisposeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-red-700">Dispose Asset</DialogTitle></DialogHeader>
          <p className="text-sm text-[#475569]">This will mark <strong>{a.name}</strong> as disposed and stop future actions. The HR history remains.</p>
          <Field label="Reason"><Textarea rows={3} value={disposeForm.reason} onChange={(e) => setDisposeForm({ ...disposeForm, reason: e.target.value })} placeholder="End of life / damaged beyond repair / sold..." data-testid="dispose-reason" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisposeOpen(false)}>Cancel</Button>
            <Button onClick={dispose} className="bg-red-700 hover:bg-red-800 text-white" data-testid="dispose-submit">Confirm Disposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <ImageLightbox open={lbOpen} onClose={() => setLbOpen(false)} images={lbImages} startIndex={lbIndex} />
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="overline text-[#9DCD3F]">{label}</div>
    <div className="text-sm text-[#1B2C5A] font-medium truncate">{value}</div>
  </div>
);

const Detail = ({ label, value, mono, full }) => (
  <div className={"p-3 rounded-lg bg-[#FDFBF7] border border-[#E2E8F0] " + (full ? "md:col-span-3" : "")}>
    <div className="overline text-[#9DCD3F]">{label}</div>
    <div className={"text-sm text-[#1B2C5A] mt-1 break-words " + (mono ? "font-mono" : "")}>{value || "—"}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default AssetProfile;
